import { Command, Option } from "clipanion";
import * as path from "node:path";
import { M8tCommand } from "../../lib/m8t-command.js";
import { colors } from "../../lib/output.js";
import { syncAgentContent } from "../../lib/agent-content-sync.js";
import { tryResolveRepoRoot } from "../../lib/brain-paths.js";

export class AgentContentSyncCommand extends M8tCommand {
  static paths = [["agent-content", "sync"]];
  static usage = Command.Usage({
    description: "Materialize the pinned agent repos into personas/ and brain-seeds/.",
    details:
      "Reads installer/release-pin.json, fetches each pinned agent repo at its pinned commit, verifies the content against the pin's contentSha, and writes personas/<persona>/ + brain-seeds/<persona>/ under the repo root. Those paths are git-ignored: the agent repos are the source of truth, and this is how a checkout gets a usable copy. Private agent repos need AGENT_CONTENT_TOKEN (or GH_TOKEN / GITHUB_TOKEN / an authed gh). See docs/architecture/AGENT-REPO-CONTRACT.md.",
    examples: [
      ["Sync into the current checkout", "$0 agent-content sync"],
      ["Sync into an explicit root", "$0 agent-content sync --root /opt/m8t/repo"],
    ],
  });

  root = Option.String("--root", { description: "Repo root to hydrate into. Defaults to the m8t checkout." });

  async executeCommand(): Promise<number> {
    const root =
      (typeof this.root === "string" ? this.root : undefined) ?? tryResolveRepoRoot() ?? process.cwd();
    const synced = await syncAgentContent({
      root: path.resolve(root),
      deps: { env: this.context.env, onNotice: (m) => this.context.stderr.write(`  ${colors.dim(m)}\n`) },
    });

    if (synced.length === 0) {
      this.context.stdout.write(`${colors.hint("note:")} the release pin declares no agents — nothing to sync.\n`);
      return 0;
    }
    for (const a of synced) {
      this.context.stdout.write(
        `${colors.success("✓")} ${colors.field(a.persona)} ← ${a.repo}@${a.ref.slice(0, 8)} ` +
          `(${colors.dim(`personas/${a.persona}/, brain-seeds/${a.persona}/`)})\n`,
      );
    }
    return 0;
  }
}
